import React, { useState } from "react";
import { Transition } from "@headlessui/react";
import {
  MinusIcon,
  PlusIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useTransitionGuide } from "./TransitionGuideContext";
import PricingSetupStep from "./steps/PricingSetupStep";
import PatientReviewStep from "./steps/PatientReviewStep";
import FirstPrescriptionStep from "./steps/FirstPrescriptionStep";

const STEP_ICONS = {
  welcome: HomeIcon,
  pricing_setup: CurrencyDollarIcon,
  patient_review: UserGroupIcon,
  first_prescription: DocumentTextIcon,
  complete: SparklesIcon,
};

export default function TransitionGuideWidget() {
  // Use a try-catch to handle cases where the context isn't available yet
  let contextValues;
  try {
    contextValues = useTransitionGuide();
  } catch (error) {
    // Context not available yet, don't render anything
    return null;
  }

  const {
    isVisible,
    isMinimized,
    currentStep,
    steps,
    progressPercent,
    completedSteps,
    totalSteps,
    completeStep,
    goToStep,
    dismissGuide,
    toggleMinimized,
    skipStep,
  } = contextValues;

  if (!isVisible) return null;

  const currentStepData = steps[currentStep];
  const StepIcon = STEP_ICONS[currentStepData?.id] || HomeIcon;

  // Minimized state - small floating indicator
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Transition
          show={true}
          enter="transform ease-out duration-300 transition"
          enterFrom="scale-95 opacity-0"
          enterTo="scale-100 opacity-100"
          leave="transition ease-in duration-200"
          leaveFrom="scale-100 opacity-100"
          leaveTo="scale-95 opacity-0"
        >
          <button
            onClick={toggleMinimized}
            className="bg-integria-green text-white rounded-full p-4 shadow-lg hover:bg-integria-green/90 transition-all group"
          >
            <div className="flex items-center space-x-2">
              <StepIcon className="h-6 w-6" />
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Setup Guide</span>
                <div className="bg-white text-integria-green rounded-full px-3 py-1 text-sm font-medium">
                  {completedSteps}/{totalSteps}
                </div>
              </div>
            </div>
            <div className="absolute bottom-full right-0 mb-2 bg-gray-900 text-white text-sm px-3 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Platform Setup Guide ({completedSteps}/{totalSteps} complete)
            </div>
          </button>
        </Transition>
      </div>
    );
  }

  // Full widget state
  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-md">
      <Transition
        show={true}
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-4 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transition ease-in duration-200"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-4 opacity-0"
      >
        <div className="bg-white rounded-lg shadow-xl ring-1 ring-black/5 overflow-hidden">
          {/* Header */}
          <div className="bg-integria-green px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <StepIcon className="h-6 w-6 text-white" />
                <div>
                  <h3 className="text-white font-medium text-sm">
                    Setup Guide
                  </h3>
                  <p className="text-green-100 text-xs">
                    Step {currentStep + 1} of {totalSteps}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleMinimized}
                  className="text-white hover:text-green-100 transition-colors"
                >
                  <MinusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-green-100 mb-1">
                <span>Progress</span>
                <span>{Math.round(progressPercent)}% complete</span>
              </div>
              <div className="w-full bg-green-600 rounded-full h-2">
                <div
                  className="bg-white h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-xl text-gray-900 font-medium mb-2">
                {currentStepData.title}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {currentStepData.description}
              </p>
            </div>

            {/* Step-specific content */}
            <div className="mb-4">
              {renderStepContent(currentStepData.id, {
                completeStep,
                skipStep,
                dismissGuide,
              })}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => goToStep(currentStep - 1)}
                disabled={currentStep === 0}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>

              <div className="flex space-x-2">
                {steps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => goToStep(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      step.completed
                        ? "bg-green-500"
                        : index === currentStep
                          ? "bg-integria-green"
                          : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => goToStep(currentStep + 1)}
                disabled={currentStep === steps.length - 1}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ArrowRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}

function renderStepContent(stepId, { completeStep, skipStep, dismissGuide }) {
  switch (stepId) {
    case "welcome":
      return (
        <div>
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <SparklesIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 text-sm text-center">
              Complete these steps to start sending prescriptions.
            </p>
          </div>
          <button
            onClick={() => completeStep("welcome")}
            className="w-full bg-integria-green text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-integria-green/90 transition-colors"
          >
            Get started
          </button>
        </div>
      );

    case "pricing_setup":
      return (
        <PricingSetupStep
          onComplete={() => completeStep("pricing_setup")}
          onSkip={() => skipStep("pricing_setup")}
        />
      );

    case "patient_review":
      return (
        <PatientReviewStep
          onComplete={() => completeStep("patient_review")}
          onSkip={() => skipStep("patient_review")}
        />
      );

    case "first_prescription":
      return (
        <FirstPrescriptionStep
          onComplete={() => completeStep("first_prescription")}
          onSkip={() => skipStep("first_prescription")}
        />
      );

    case "complete":
      return (
        <div className="text-center">
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 text-sm font-medium mb-1">
              Setup Complete! 🎉
            </p>
            <p className="text-green-700 text-sm mb-3">
              Need help?{" "}
              <a href="mailto:enquiries@integria.com">
                Email us at enquiries@integria.com
              </a>
            </p>
          </div>
          <button
            onClick={dismissGuide}
            className="w-full bg-integria-green text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-integria-green/90 transition-colors"
          >
            Close Guide
          </button>
        </div>
      );

    default:
      return (
        <div className="text-center text-gray-500 text-sm">
          Step content not found
        </div>
      );
  }
}
